import React, { useState } from "react";
import { Lock, X } from "lucide-react";
import { InlineAlert } from "./InlineAlert";
import { Spinner } from "./Spinner";

export type PasswordModalMode = "private" | "edit";

type PasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (password: string) => Promise<void>;
  mode: PasswordModalMode;
  structureName?: string;
  allowCancel?: boolean;
  showViewOption?: boolean;
  onViewOnly?: () => void;
};

export const PasswordModal: React.FC<PasswordModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  structureName = "diese Struktur",
  allowCancel = true,
  showViewOption = false,
  onViewOnly,
}) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password.trim()) {
      setError("Bitte Passwort eingeben");
      return;
    }

    setLoading(true);
    try {
      await onSubmit(password);
      setPassword("");
      onClose();
    } catch (err: any) {
      console.error("Auth error:", err);
      const message = err.message || "Authentifizierung fehlgeschlagen";
      if (message.includes("Incorrect password") || message.includes("401")) {
        setError("Falsches Passwort");
      } else if (message.includes("Too many") || message.includes("429")) {
        setError("Zu viele Versuche. Bitte später erneut versuchen.");
      } else {
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPassword("");
    setError(null);
    onClose();
  };

  const title = mode === "private" ? "Privates Organigramm" : "Bearbeiten freischalten";
  const description =
    mode === "private"
      ? `"${structureName}" ist privat. Passwort erforderlich.`
      : `Für Bearbeiten von "${structureName}" ist ein Passwort erforderlich.`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
              <Lock className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{title}</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{description}</p>
            </div>
          </div>
          {allowCancel && (
            <button
              onClick={handleCancel}
              className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Error Alert */}
        {error && (
          <InlineAlert tone="error" message={error} dismissible onClose={() => setError(null)} className="mb-4" />
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Passwort</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Passwort eingeben..."
              autoFocus
              disabled={loading}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !password.trim()}
              className="flex-1 btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? <Spinner label="Überprüfen..." size="sm" className="justify-center" /> : mode === "private" ? "Entsperren" : "Freischalten"}
            </button>

            {showViewOption && onViewOnly && (
              <button type="button" onClick={onViewOnly} className="btn btn-secondary" disabled={loading}>
                Nur ansehen
              </button>
            )}

            {allowCancel && (
              <button type="button" onClick={handleCancel} className="btn btn-secondary" disabled={loading}>
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
